import {Box, Flex, Heading, Tag, Text, VStack} from "@chakra-ui/react";
import * as React from "react";
import type {CSSProperties} from "react";
import dagre from "dagre";
import ReactFlow, {Background, Controls, Edge, MarkerType, Node} from "reactflow";
import "reactflow/dist/style.css";
import {Spot, TriggerRule} from "../../components/types";
import {ChevronRightIcon} from "@chakra-ui/icons";
import CustomFlowPanel from "./CustomFlowPanel";

type FlowEdge = {
	fromId: string;
	toId: string;
	fromLabel: string;
	toLabel: string;
	reason?: string;
};

type FlowConstraint = {
	blockerId: string;
	targetId: string;
	blockerLabel: string;
	targetLabel: string;
	reason: string;
};

type Props = {
	areaName?: string;
	spots: Spot[];
	versionLabel?: string;
	flowView?: "reactflow" | "custom";
};

const NODE_WIDTH = 150;
const NODE_HEIGHT = 50;

const isPositiveEvent = (eventType: TriggerRule["eventType"]) => {
	const val = String(eventType);
	return val === "SPOT_TRIGGERED" || val === "SPOT_FINISHED" || val === "SPOT_FINISHED_FOR_MS"
		|| val === "AMBIENT_TRIGGERED" || val === "AMBIENT_TIME_SINCE_TRIGGERED";
};

const isNegativeEvent = (eventType: TriggerRule["eventType"]) => {
	const val = String(eventType);
	return val === "SPOT_NOT_TRIGGERED" || val === "SPOT_NOT_FINISHED"
		|| val === "AMBIENT_NOT_TRIGGERED";
};

const getRuleReason = (rule: TriggerRule) => {
	if (rule.type === "Ambient") {
		if (rule.eventType === "AMBIENT_TRIGGERED") return "ambient zone must be triggered";
		if (rule.eventType === "AMBIENT_NOT_TRIGGERED") return "ambient zone must not be triggered";
		if (rule.eventType === "AMBIENT_TIME_SINCE_TRIGGERED") return "time since ambient zone triggered";
		return "ambient zone condition";
	}
	if (isPositiveEvent(rule.eventType)) {
		return "must be triggered/finished before";
	}
	if (isNegativeEvent(rule.eventType)) {
		return "must not have been triggered/finished before";
	}
	return "affects playback";
};

const layoutGraph = (nodes: Node[], edges: Edge[]) => {
	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));
	dagreGraph.setGraph({rankdir: "LR", ranker: "longest-path"});

	const nodeIds = new Set(nodes.map((node) => node.id));

	nodes.forEach((node) => {
		dagreGraph.setNode(node.id, {width: NODE_WIDTH, height: NODE_HEIGHT});
	});
	edges
		.filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target))
		.forEach((edge) => {
			dagreGraph.setEdge(edge.source, edge.target);
		});

	dagre.layout(dagreGraph);

	const laidOutNodes = nodes.map((node) => {
		const pos = dagreGraph.node(node.id);
		return {
			...node,
			position: {
				x: pos.x - NODE_WIDTH / 2,
				y: pos.y - NODE_HEIGHT / 2
			}
		};
	});

	return {nodes: laidOutNodes, edges};
};

const MapDrawerContent: React.FC<Props> = ({areaName, spots, versionLabel, flowView = "reactflow"}) => {
	const [isCustomFlowExpanded, setIsCustomFlowExpanded] = React.useState(false);
	const spotByKey = React.useMemo(() => {
		const map = new Map<string, Spot>();
		spots.forEach((spot) => map.set(spot.key, spot));
		return map;
	}, [spots]);

	const positiveEdges: FlowEdge[] = [];
	const blockingConstraints: FlowConstraint[] = [];
	const positivePrereqs = new Set<string>();

	spots.forEach((spot) => {
		const rules = spot.triggerRules?.rules || [];
		rules.forEach((rule: TriggerRule) => {
			if (rule.type === "Spot" && rule.spotKey) {
				const source = spotByKey.get(rule.spotKey);
				if (!source) {
					return;
				}
				const fromLabel = source.name || source.key;
				const toLabel = spot.name || spot.key;
				if (isPositiveEvent(rule.eventType)) {
					positivePrereqs.add(spot.key);
					positiveEdges.push({
						fromId: source.key,
						toId: spot.key,
						fromLabel,
						toLabel,
						reason: "Requires trigger/finish",
					});
				} else if (isNegativeEvent(rule.eventType)) {
					blockingConstraints.push({
						blockerId: source.key,
						targetId: spot.key,
						blockerLabel: fromLabel,
						targetLabel: toLabel,
						reason: "Cannot have triggered/finished",
					});
				}
			}
			// Ambient rules don't create spot-to-spot edges but are shown in summaries
		});
	});

	const startNodes = React.useMemo(() => {
		return spots
			.filter((spot) => !positivePrereqs.has(spot.key))
			.map((spot) => ({
				id: spot.key,
				label: spot.name || spot.key,
			}));
	}, [positivePrereqs, spots]);

	const ruleSummaryRows = React.useMemo(() => {
		return spots
			.map((spot) => {
				const rules = spot.triggerRules?.rules || [];
				return rules
					.filter((rule) => (rule.type === "Spot" && !!rule.spotKey) || (rule.type === "Ambient" && !!rule.zoneKey))
					.map((rule) => {
						let sourceName: string;
						if (rule.type === "Ambient") {
							sourceName = rule.zoneKey || "Unknown zone";
						} else {
							const source = spotByKey.get(rule.spotKey || "");
							sourceName = source?.name || rule.spotKey || "Unknown spot";
						}
						return {
							target: spot.name || spot.key,
							condition: rule.eventType,
							source: sourceName,
							reason: getRuleReason(rule),
						};
					});
			})
			.flat();
	}, [spots, spotByKey]);

	const {rfNodes, rfEdges} = React.useMemo(() => {
		const spotsWithKeys = spots
			.map((spot) => {
				const safeKey = spot.key || spot.id || spot.name;
				if (!safeKey) return undefined;
				return {
					...spot,
					key: safeKey
				};
			})
			.filter((s): s is Spot => !!s);

		const startNodeStyle: CSSProperties = {
			background: "#2b6cb0",
			color: "#fff",
			border: "1px solid #1a365d",
			borderRadius: 10,
			padding: 8,
			fontWeight: 600,
			width: NODE_WIDTH,
		};

		const spotNodeStyle: CSSProperties = {
			background: "#ebf8ff",
			color: "#1a365d",
			border: "1px solid #2b6cb0",
			borderRadius: 16,
			padding: 8,
			fontWeight: 600,
			width: NODE_WIDTH,
			textAlign: "center",
		};

			const nodes: Node[] = [
				{
					id: "start",
					data: {label: "Start"},
					position: {x: 0, y: 0},
					style: startNodeStyle,
				},
				...spotsWithKeys.map((spot) => ({
					id: spot.key,
					data: {label: spot.name || spot.key},
					position: {x: 0, y: 0},
					style: spotNodeStyle
				}))
		];

		const edges: Edge[] = [];
		startNodes.forEach((node, idx) => {
			edges.push({
				id: `start-${node.id}-${idx}`,
				source: "start",
				target: node.id,
				type: "smoothstep",
				style: {stroke: "#2c5282", strokeWidth: 2},
				markerEnd: {
					type: MarkerType.ArrowClosed,
					color: "#2c5282"
				}
			});
		});

		positiveEdges
			.filter((edge) => edge.fromId && edge.toId)
			.forEach((edge, idx) => {
				const label = edge.reason?.toLowerCase().includes("finish") ? "Require full play" : "Require triggered";
				edges.push({
					id: `edge-${edge.fromId}-${edge.toId}-${idx}`,
					source: edge.fromId,
					target: edge.toId,
					type: "smoothstep",
					label,
					labelBgBorderRadius: 6,
					labelBgPadding: [4, 8],
					labelBgStyle: {fill: "#edf2f7"},
					style: {stroke: "#2c5282", strokeWidth: 2},
					markerEnd: {
						type: MarkerType.ArrowClosed,
						color: "#2c5282"
					}
				});
			});

		const layouted = layoutGraph(nodes, edges);
		return {rfNodes: layouted.nodes, rfEdges: layouted.edges};
	}, [positiveEdges, spots, startNodes]);

	React.useEffect(() => {
		setIsCustomFlowExpanded(false);
	}, [flowView]);

	const showDetails = flowView === "reactflow" || !isCustomFlowExpanded;

	return (
		<Box
			flex={1}
			overflowY={flowView === "custom" && isCustomFlowExpanded ? "hidden" : "auto"}
			padding={"20px"}
			color={"gray.700"}
			display={"flex"}
			flexDirection={"column"}
			gap={6}
			minHeight={0}
		>
			<Box>
				<Heading size={"sm"} color={"gray.800"} lineHeight={1.3}>
					{areaName || "Space overview"}
				</Heading>
				{versionLabel && (
					<Text fontSize={"xs"} color={"gray.500"} mt={1}>
						Version: {versionLabel}
					</Text>
				)}
			</Box>

			{flowView === "reactflow" ? (
				<Box border={"1px solid #e2e8f0"} borderRadius={"md"} padding={3} bg={"white"} boxShadow={"sm"}>
					<Text fontSize={"sm"} fontWeight={600} mb={3}>Possible routes</Text>
					<Box border={"1px solid #e2e8f0"} borderRadius={"md"} bg={"gray.50"} height={"360px"} minHeight={"280px"}>
						<ReactFlow
							nodes={rfNodes}
							edges={rfEdges}
							fitView
							minZoom={0.5}
							maxZoom={1.4}
						>
							<Background gap={12} size={1} color={"#e2e8f0"}/>
							<Controls position={"bottom-right"}/>
						</ReactFlow>
					</Box>
				</Box>
			) : (
				<CustomFlowPanel
					spots={spots}
					expanded={isCustomFlowExpanded}
					onToggleExpand={() => setIsCustomFlowExpanded((prev) => !prev)}
				/>
			)}

			{showDetails ? (
				<>
					<Box border={"1px solid #e2e8f0"} borderRadius={"md"} padding={3} bg={"gray.50"}>
						<Text fontSize={"sm"} fontWeight={600} mb={2}>Rules in play</Text>
						{ruleSummaryRows.length === 0 ? (
							<Text fontSize={"sm"} color={"gray.600"}>No spot-to-spot rules configured.</Text>
						) : (
							<VStack align={"stretch"} spacing={2}>
								{ruleSummaryRows.map((row, idx) => (
									<Flex key={`${row.source}-${row.target}-${idx}`} align={"center"} gap={2} wrap={"wrap"}>
										<Tag size={"sm"} colorScheme={"gray"}>{row.source}</Tag>
										<Text fontSize={"sm"} color={"gray.700"}>{row.reason}</Text>
										<Tag size={"sm"} colorScheme={"blue"}>{row.target}</Tag>
									</Flex>
								))}
							</VStack>
						)}
					</Box>

					<Box border={"1px solid #e2e8f0"} borderRadius={"md"} padding={3} bg={"white"} boxShadow={"sm"}>
						<Text fontSize={"sm"} fontWeight={600} mb={3}>Possible flow</Text>
						<VStack align={"stretch"} spacing={3}>
							<Flex align={"center"} gap={2} wrap={"wrap"}>
								<Tag colorScheme={"green"}>Start</Tag>
								<Text fontSize={"sm"} color={"gray.600"}>can lead to</Text>
								{startNodes.length === 0 ? (
									<Tag colorScheme={"gray"}>No direct entries</Tag>
								) : (
									<Flex gap={2} wrap={"wrap"}>
										{startNodes.map((node) => (
											<Tag key={`start-${node.id}`} colorScheme={"blue"}>{node.label}</Tag>
										))}
									</Flex>
								)}
							</Flex>

							{positiveEdges.length === 0 ? (
								<Text fontSize={"sm"} color={"gray.600"}>No prerequisite links between spots.</Text>
								) : (
									positiveEdges.map((edge, idx) => (
										<Flex key={`${edge.fromId}-${edge.toId}-${idx}`} align={"center"} gap={2} wrap={"wrap"}>
											<Tag colorScheme={"gray"}>{edge.fromLabel}</Tag>
											<ChevronRightIcon color={"gray.600"}/>
											<Tag colorScheme={"blue"}>{edge.toLabel}</Tag>
											<Tag size={"sm"} colorScheme={"green"} variant={"subtle"}>
												{edge.reason?.toLowerCase().includes("finish") ? "Require full play" : "Require triggered"}
											</Tag>
										</Flex>
									))
								)}

							{blockingConstraints.length > 0 && (
								<Box mt={2}>
									<Text fontSize={"sm"} fontWeight={600} color={"gray.700"} mb={2}>Blocked flows</Text>
									<VStack align={"stretch"} spacing={2}>
										{blockingConstraints.map((constraint, idx) => (
											<Flex key={`${constraint.blockerId}-${constraint.targetId}-${idx}`} align={"center"} gap={2} wrap={"wrap"}>
												<Tag colorScheme={"red"}>{constraint.blockerLabel}</Tag>
												<ChevronRightIcon color={"red.400"}/>
												<Tag colorScheme={"gray"}>{constraint.targetLabel}</Tag>
												<Tag size={"sm"} colorScheme={"red"} variant={"subtle"}>{constraint.reason}</Tag>
											</Flex>
										))}
									</VStack>
								</Box>
							)}
						</VStack>
					</Box>
				</>
			) : null}
		</Box>
	);
};

export default MapDrawerContent;
